import { getErrorMessage } from './error-utils';

type GTagEvent = {
  action: string;
  category: string;
  label?: string;
  value?: number;
};

// GA4 Measurement ID (G-XXXXXXXXXX format) for Google Analytics 4
export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA4_ID;
// Google Ads Conversion ID (AW-XXXXXXXX format) for Google Ads conversions
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
export const GOOGLE_ADS_SIGNUP_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL;
export const GOOGLE_ADS_PURCHASE_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL;
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
export const KLAVIYO_PUBLIC_KEY = process.env.NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY;

// Helper function to check cookie consent (GDPR compliance)
const hasCookieConsent = (): boolean => {
  return document.cookie.includes('cookieConsent=true');
};

// Global lock for PageView to prevent race conditions
const acquirePageViewLock = (url: string): boolean => {
  if (typeof window === 'undefined') return false;
  if (!(window as any).__pageViewLocks) {
    (window as any).__pageViewLocks = {};
  }
  const lockKey = `pv_${url}`;
  if ((window as any).__pageViewLocks[lockKey]) {
    return false; // Already locked
  }
  (window as any).__pageViewLocks[lockKey] = Date.now();
  return true;
};

// Release lock after a delay to account for async fbq calls
const releasePageViewLockDelayed = (url: string, delayMs = 1000) => {
  setTimeout(() => {
    if (typeof window !== 'undefined' && (window as any).__pageViewLocks) {
      delete (window as any).__pageViewLocks[`pv_${url}`];
    }
  }, delayMs);
};

const releasePageViewLock = (url: string) => {
  if (typeof window !== 'undefined' && (window as any).__pageViewLocks) {
    delete (window as any).__pageViewLocks[`pv_${url}`];
  }
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string) => {
  // Reliance on Consent Mode for Google (gtag.js)
  // Meta Pixel requires explicit consent check (no Consent Mode equivalent)

  // Try to acquire lock - only one concurrent call should proceed
  if (!acquirePageViewLock(url)) return;

  // Google Analytics - relies on Consent Mode
  if (typeof window.gtag !== 'undefined' && GA_TRACKING_ID) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }

  // Meta Pixel PageView - requires explicit consent check for GDPR compliance
  if (hasCookieConsent() && typeof window.fbq !== 'undefined' && FB_PIXEL_ID) {
    window.fbq('track', 'PageView');
  }

  // Release lock after a delay to account for async fbq calls
  releasePageViewLockDelayed(url, 1000);
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const event = (action: string, { event_category, event_label, value }: any) => {
  if (typeof window.gtag !== 'undefined' && GA_TRACKING_ID) {
    window.gtag('event', action, {
      event_category,
      event_label,
      value,
    });
  }
};

export const trackSignUp = (method: string, userData?: { email?: string | null }) => {
  console.log(`[Analytics] trackSignUp called with method: ${method}`, userData);
  // Google Analytics/Ads - merged event with send_to
  if (typeof window.gtag !== 'undefined') {
    const sendTo = [];
    if (GA_TRACKING_ID) sendTo.push(GA_TRACKING_ID);
    if (GOOGLE_ADS_ID && GOOGLE_ADS_SIGNUP_LABEL) {
      window.gtag('config', GOOGLE_ADS_ID, { send_page_view: false });
      sendTo.push(`${GOOGLE_ADS_ID}/${GOOGLE_ADS_SIGNUP_LABEL}`);
    }

    if (userData?.email) {
      window.gtag('set', 'user_data', {
        email: userData.email,
      });
    }

    if (sendTo.length > 0) {
      window.gtag('event', 'sign_up', {
        method: method,
        send_to: sendTo,
      });
    }
  }

  // Meta Pixel CompleteRegistration - requires explicit consent check for GDPR compliance
  if (hasCookieConsent() && typeof window.fbq !== 'undefined' && FB_PIXEL_ID) {
    window.fbq('track', 'CompleteRegistration', {
      content_name: method,
      status: true,
      ...(userData?.email && { user_data: { em: userData.email } }),
    });
  }

  // Klaviyo - track sign up event
  if (typeof window !== 'undefined' && KLAVIYO_PUBLIC_KEY) {
    try {
      // @ts-ignore
      if (window._klOnsite) {
        // Enforce plain object to prevent toJSON/serialization errors
        const payload = {
          method: String(method),
          $email: userData?.email ? String(userData.email) : undefined,
        };
        // @ts-ignore
        window._klOnsite.push(['track', 'Signed Up', payload]);
      }
    } catch (e) {
      // console.error('[Analytics] Klaviyo sign_up error:', e);
    }
  }
};

export const trackPurchase = (transactionId: string, value: number, currency = 'GBP') => {
  // Google Analytics/Ads - merged event with send_to
  if (typeof window.gtag !== 'undefined') {
    const sendTo = [];
    if (GA_TRACKING_ID) sendTo.push(GA_TRACKING_ID);
    if (GOOGLE_ADS_ID && GOOGLE_ADS_PURCHASE_LABEL) {
      window.gtag('config', GOOGLE_ADS_ID, { send_page_view: false });
      sendTo.push(`${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`);
    }

    if (sendTo.length > 0) {
      window.gtag('event', 'purchase', {
        transaction_id: transactionId,
        value: value,
        currency: currency,
        send_to: sendTo,
      });
    }
  }

  // Meta Pixel Purchase - requires explicit consent check for GDPR compliance
  if (hasCookieConsent() && typeof window.fbq !== 'undefined' && FB_PIXEL_ID) {
    window.fbq('track', 'Purchase', {
      value: value,
      currency: currency,
      content_ids: [transactionId],
      content_type: 'product',
    });
  }
};

export const trackSignIn = (method: string, userData?: { email?: string | null }) => {
  // Google Analytics - relies on Consent Mode for GDPR

  // GA4 login event
  if (typeof window.gtag !== 'undefined' && GA_TRACKING_ID) {
    window.gtag('event', 'login', {
      method: method,
    });
  }

  // Meta Pixel Login - requires explicit consent check for GDPR compliance
  if (hasCookieConsent() && typeof window.fbq !== 'undefined' && FB_PIXEL_ID) {
    window.fbq('trackCustom', 'login', {
      content_name: method,
      status: true,
      ...(userData?.email && { user_data: { em: userData.email } }),
    });
  }

  // Klaviyo - track sign in event
  if (typeof window !== 'undefined' && KLAVIYO_PUBLIC_KEY) {
    try {
      // @ts-ignore
      if (window._klOnsite) {
        // Enforce plain object
        const payload = {
          method: String(method),
          $email: userData?.email ? String(userData.email) : undefined,
        };
        // @ts-ignore
        window._klOnsite.push(['track', 'Signed In', payload]);
      }
    } catch (e) {
      // console.error('[Analytics] Klaviyo sign_in error:', e);
    }
  }
};
